import React, { FC } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import { system } from 'styled-system';

import BaseBox from '../../internal/components/BaseBox';
import { rgba } from '../../internal/styles/colors';
import semanticColorVariant from '../../internal/styles/semanticColorVariant';
import sx from '../../internal/styles/sx';
import { ScaleValue, WithDivProps } from '../../internal/styles/types';
import { PillProps, ThemedPillProps } from './types';
import { typeVariant } from './variant';

const pxStyles = ({ borderWidth }: { borderWidth: number }): CSSObject => {
  return {
    ...(borderWidth && {
      borderWidth,
      borderStyle: 'solid',
    }),
  };
};
const defaultSpaceStyles = pxStyles({ borderWidth: 0 });
const softSpaceStyles = pxStyles({ borderWidth: 1 });

const BasePill = styled(BaseBox.withComponent('span'), {
  shouldForwardProp: (prop) => shouldForwardProp(prop) && prop !== 'type',
})<ThemedPillProps>(
  css({
    px: 2,
    fontSize: 'bodies.small',
    lineHeight: 'bodies.small',
    backgroundColor: 'transparent',
    display: 'inline-block',
    cursor: 'default',
  }),
  system({
    rounded: {
      property: 'borderRadius',
      scale: 'radii',
      transform: (value, scale = []): ScaleValue => scale[value ? 3 : 1],
    },
  }),
  (props) =>
    semanticColorVariant((color) =>
      props.hoverable
        ? {
            ':hover': {
              backgroundColor: rgba(color, 0.3),
            },
          }
        : {}
    )(props)
);

const DefaultPill = styled(BasePill)<ThemedPillProps>(
  defaultSpaceStyles,
  ({ type }) =>
    css({
      backgroundColor: `pill.default.${type}.bg`,
      color: `pill.default.${type}.color`,
    }),
  typeVariant,
  sx
);
const SoftPill = styled(BasePill)<ThemedPillProps>(
  defaultSpaceStyles,
  ({ type }) =>
    css({
      backgroundColor: `pill.soft.${type}.bg`,
      color: `pill.soft.${type}.color`,
    }),
  sx
);

const GhostPill = styled(BasePill)<ThemedPillProps>(
  softSpaceStyles,
  ({ type }) =>
    css({
      borderColor: `pill.ghost.${type}.borderColor`,
      color: `pill.ghost.${type}.color`,
    }),
  sx
);

const themeComponents = {
  ghost: GhostPill,
  soft: SoftPill,
};

const Pill: FC<WithDivProps<PillProps>> = ({
  theme = 'default',
  rounded = false,
  ...props
}) => {
  const ComponentPill = themeComponents[theme] || DefaultPill;
  return <ComponentPill rounded={rounded} {...props} />;
};

export default Pill;
